/* eslint-disable react/require-default-props */
import React from 'react';
import { createPortal } from 'react-dom';

import type { ProposalDocumentBidding } from 'components/molecules/ai_management_proposal_document';
import { useProposalShell } from 'components/organisms/ai_management_bidding_proposal/context';
import { useProposalGeneration } from 'hooks/useProposalGeneration';
import type { ProposalResponse } from 'services/actions/ai_management_proposal';
import { formatDateTime } from 'services/ai_management_dates';
import type { Proposal } from 'services/ai_management_proposal';

interface AiManagementProposalGenerateProps {
  biddingId: number;
  bidding: ProposalDocumentBidding;
  proposal: Proposal | null;
  pendencias: number;
  // null enquanto a checagem carrega: bloqueia sem mensagem.
  checagem: { podeGerar: boolean; naoAtendidas: number } | null;
  onRegistered: (data: ProposalResponse) => void;
  onFilesUploaded?: () => void;
}

const AiManagementProposalGenerate: React.FC<AiManagementProposalGenerateProps> = ({
  biddingId,
  bidding,
  proposal,
  pendencias,
  checagem,
  onRegistered,
  onFilesUploaded,
}) => {
  const { footerActions } = useProposalShell();
  const { generating, error, failedCount, generate, retry } = useProposalGeneration(
    biddingId,
    bidding,
    { onRegistered, onFilesUploaded },
  );
  const temGeracao = (proposal?.generatedFiles.length ?? 0) > 0;
  const gerada = proposal?.status === 'gerada' && temGeracao;
  const bloqueadaPelaChecagem = !!checagem && !checagem.podeGerar;
  const bloqueada = pendencias > 0 || !checagem || bloqueadaPelaChecagem;

  const footer = (
    <>
      {bloqueada && !generating && checagem && (
        <small className="footer-hint">
          Resolva as pendências listadas acima para gerar a proposta
        </small>
      )}
      <button
        type="button"
        className="btn btn-primary"
        disabled={generating || bloqueada}
        onClick={generate}
      >
        {generating && 'Gerando…'}
        {!generating && (temGeracao ? 'Gerar de novo' : 'Gerar proposta')}
      </button>
    </>
  );

  return (
    <div>
      {footerActions && createPortal(footer, footerActions)}
      {temGeracao && proposal && (
        <div className={`alert ${gerada ? 'alert-success' : 'alert-warning'} mb-3`}>
          <strong>{gerada ? 'Proposta gerada' : 'Última proposta gerada'}</strong>
          {proposal.generatedAt ? ` em ${formatDateTime(proposal.generatedAt)}` : ''}
          {!gerada && (
            <span className="d-block">
              A proposta mudou depois dessa geração: gere de novo para atualizar os arquivos.
            </span>
          )}
        </div>
      )}

      {error && (
        <div className="alert alert-danger d-flex align-items-center justify-content-between mt-3 mb-0">
          <span>
            {error}
            {failedCount > 0 &&
              ` (${failedCount} ${
                failedCount === 1 ? 'arquivo não foi salvo' : 'arquivos não foram salvos'
              })`}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger ml-3 flex-shrink-0"
            disabled={generating}
            onClick={retry}
          >
            Tentar de novo
          </button>
        </div>
      )}
    </div>
  );
};

export default AiManagementProposalGenerate;
